import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { database } from "../firebaseConfig";
import { ref, get } from "firebase/database";
import { useNavigate, Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import SearchBar from "../components/SearchBar";
import "./Home.css";
import { config } from "../config/config";
const API_URL = config.baseUrl;

const HomePage = () => {
    const navigate = useNavigate();

    // Initialize responsive state
    const [cardsPerRow, setCardsPerRow] = useState(() =>
        window.innerWidth <= 768 ? 2 : 3
    );
    const [procardsPerRow, setProCardsPerRow] = useState(() =>
        window.innerWidth <= 768 ? 2 : 4
    );
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

    // Search functionality states
    const [searchTerm, setSearchTerm] = useState("");
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [searchResults, setSearchResults] = useState([]);

    const [homeConfig, setHomeConfig] = useState(null);
    const [categories, setCategories] = useState([]);
    const [topProduct, setTopProduct] = useState([]);
    const [topProductsData, setTopProductsData] = useState([]);

    // Update view on resize
    useEffect(() => {
        const updateCardsPerRow = () => {
            const mobile = window.innerWidth <= 768;
            setCardsPerRow(mobile ? 2 : 3);
            setProCardsPerRow(mobile ? 2 : 4);
            setIsMobile(mobile);
        };
        updateCardsPerRow();
        window.addEventListener("resize", updateCardsPerRow);
        return () => window.removeEventListener("resize", updateCardsPerRow);
    }, []);

    // Fetch home config from Firebase
    useEffect(() => {
        const fetchHomeConfig = async () => {
            try {
                const snapshot = await get(ref(database, "homeconfig"));
                if (snapshot.exists()) {
                    setHomeConfig(snapshot.val());
                }
            } catch (error) {
                console.error("Error fetching home config:", error);
            }
        };
        fetchHomeConfig();
    }, []);

    // Fetch categories from Firebase
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const snapshot = await get(ref(database, "categories"));
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const catArray = Array.isArray(data) ? data : Object.values(data);
                    setTopProduct(catArray);
                    setCategories(catArray.slice(0, cardsPerRow * 2));
                } else {
                    setCategories([]);
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
            }
        };
        fetchCategories();
    }, [cardsPerRow]);

    // Compute top products
    useEffect(() => {
        if (homeConfig && homeConfig.topProducts) {
            const skuSet = new Set(homeConfig.topProducts.filter((sku) => sku));
            const topProducts = [];
            topProduct.forEach((category) => {
                if (category.products && Array.isArray(category.products)) {
                    category.products.forEach((product) => {
                        if (skuSet.has(product.sku)) {
                            topProducts.push(product);
                        }
                    });
                }
            });
            setTopProductsData(topProducts);
        }
    }, [homeConfig, topProduct]);

    // Handle search functionality
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setShowSearchResults(false);
            return;
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        let results = [];

        // Search all categories
        topProduct.forEach((cat) => {
            if (cat.products) {
                const matches = cat.products.filter(
                    (prod) => prod.name && prod.name.toLowerCase().includes(lowerSearchTerm)
                );
                results = [...results, ...matches];
            }
        });

        setSearchResults(results);
        setShowSearchResults(results.length > 0);
    }, [searchTerm, categories, topProduct]);

    if (!homeConfig) {
        return (
            <div className="loading-container" style={{ width: "100%" }}>
                <img
                    src={`${API_URL}uploads/loading.gif`}
                    alt="Loading..."
                    className="loading-gif"
                    style={{ width: "100%" }}
                />
            </div>
        );
    }

    const {
        videoUrl,
        phoneVideoUrl,
        whyUsVideoUrl,
        whyUsContent,
        promoVideoUrl,
        bodyBackgroundColor,
        sectionConfig,
        seo,
    } = homeConfig;

    const heroVideoUrl = isMobile && phoneVideoUrl ? phoneVideoUrl : videoUrl;

    const sections = [
        {
            key: "hero",
            order: sectionConfig?.hero?.order || 1,
            backgroundColor: sectionConfig?.hero?.backgroundColor || "#ffffff",
            content: (
                <div className="home-container">
                    <div className="home-text-overlay">
                        <h1 className="home-main-heading">
                            <span className="home-text-black">Expert Care for</span> <br />
                            <span className="home-text-purple">Women's Health</span>
                        </h1>
                        <p className="home-description-text">
                            Trusted by doctors, designed for you. We bring you safe, effective
                            gynecological medicines to support hormonal balance, menstrual
                            health, fertility, and intimate care all backed by science and
                            expert recommendations.
                        </p>
                        <div
                            className="home-additional-info"
                            style={{
                                marginTop: "20px",
                            }}
                        >
                            <p
                                className="home-info-item"
                                style={{
                                    marginBottom: "10px",
                                    fontSize: "1rem",
                                    color: "#333",
                                }}
                            >
                                Clinically Tested & Doctor-Approved
                            </p>
                            <p
                                className="home-info-item"
                                style={{
                                    marginBottom: "10px",
                                    fontSize: "1rem",
                                    color: "#333",
                                }}
                            >
                                Safe, Effective, and Fast-Acting
                            </p>
                            <p
                                className="home-info-item"
                                style={{
                                    marginBottom: "10px",
                                    fontSize: "1rem",
                                    color: "#333",
                                }}
                            >
                                Supporting Women's Health at Every Stage
                            </p>
                            <p
                                className="home-info-item"
                                style={{
                                    marginBottom: "10px",
                                    fontSize: "1rem",
                                    color: "#333",
                                }}
                            >
                                Certified by ISO and GMP
                            </p>

                            <div
                                style={{
                                    marginTop: "10px",
                                    display: "flex",
                                    gap: "10px",
                                    alignItems: "center",
                                }}
                            >
                                <img
                                    src="/ISO.png"
                                    alt="ISO Certified"
                                    style={{
                                        width: "40px",
                                        height: "40px",
                                        objectFit: "contain",
                                    }}
                                />
                                <img
                                    src="/gmp.webp"
                                    alt="GMP Certified"
                                    style={{
                                        width: "40px",
                                        height: "40px",
                                        objectFit: "contain",
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="home-video-container">
                        {!heroVideoUrl ? (
                            <div className="home-fallback-background" />
                        ) : (
                            <video autoPlay loop muted playsInline>
                                <source src={heroVideoUrl} type="video/mp4" />
                            </video>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: "whyChooseUs",
            order: sectionConfig?.whyChooseUs?.order || 2,
            backgroundColor: sectionConfig?.whyChooseUs?.backgroundColor || "#f7f7f7",
            content: (
                <div className="home-why-choose-us">
                    {whyUsVideoUrl && (
                        <div className="home-why-video-container">
                            <video className="home-why-video" autoPlay loop muted playsInline>
                                <source src={whyUsVideoUrl} type="video/mp4" />
                            </video>
                        </div>
                    )}
                    <h2 className="home-why-title">Why Choose Us?</h2>
                    <p className="home-why-subtitle">Because Women's Health Matters</p>
                    <div
                        className="categories-list"
                        style={{ gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)` }}
                    >
                        {(isMobile
                            ? whyUsContent.length % 2 !== 0
                                ? whyUsContent.slice(0, whyUsContent.length - 1)
                                : whyUsContent
                            : whyUsContent
                        ).map((content, index) =>
                            isMobile ? (
                                <ProductCard
                                    key={index}
                                    title={content.category}
                                    mediaUrl={
                                        content.categoryUrl || "https://via.placeholder.com/300x200"
                                    }
                                    mediaType="image"
                                    edit={false}
                                    cardsPerRow={cardsPerRow}
                                    cardsPerRowPhoneView={2}
                                    onClick={() => navigate(`/OurProducts`)}
                                />
                            ) : (
                                <ProductCard
                                    key={index}
                                    title={content.category}
                                    description={content.description || ""}
                                    mediaUrl={
                                        content.categoryUrl || "https://via.placeholder.com/300x200"
                                    }
                                    mediaType="image"
                                    edit={false}
                                    cardsPerRow={cardsPerRow}
                                    cardsPerRowPhoneView={2}
                                    onClick={() => navigate(`/OurProducts`)}
                                />
                            )
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: "ourCategories",
            order: sectionConfig?.ourCategories?.order || 3,
            backgroundColor: sectionConfig?.ourCategories?.backgroundColor || "#ffffff",
            content: (
                <div className="home-categories-section">
                    <h2 className="home-categories-title">Our Categories</h2>
                    <div
                        className="categories-list"
                        style={{ gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)` }}
                    >
                        {categories.map((cat, index) => (
                            <ProductCard
                                key={index}
                                title={cat.category}
                                description={cat.description || ""}
                                price={cat.price || ""}
                                discount={cat.discount || ""}
                                rating={cat.rating || ""}
                                mediaUrl={cat.categoryUrl || "https://via.placeholder.com/300x200"}
                                mediaType="image"
                                onClick={() =>
                                    navigate(`/category/${encodeURIComponent(cat.category)}`)
                                }
                                cardsPerRow={cardsPerRow}
                                cardsPerRowPhoneView={2}
                            />
                        ))}
                    </div>
                </div>
            ),
        },
        {
            key: "promoVideo",
            order: sectionConfig?.promoVideo?.order || 4,
            backgroundColor: sectionConfig?.promoVideo?.backgroundColor || "#000000",
            content: (
                promoVideoUrl && (
                    <div
                        className="home-promo-video-section"
                        style={{ marginTop: "20px", marginBottom: "4%" }}
                    >
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            style={{ width: "100%", display: "block" }}
                        >
                            <source src={promoVideoUrl} type="video/mp4" />
                        </video>
                    </div>
                )
            ),
        },
        {
            key: "topProducts",
            order: sectionConfig?.topProducts?.order || 5,
            backgroundColor: sectionConfig?.topProducts?.backgroundColor || "#ffffff",
            content: (
                topProductsData.length > 0 && (
                    <div className="home-categories-section">
                        <h2 className="home-categories-title">Top Products</h2>
                        <div
                            className="categories-list"
                            style={{ gridTemplateColumns: `repeat(${procardsPerRow}, 1fr)` }}
                        >
                            {topProductsData.map((product, index) => (
                                <ProductCard
                                    key={index}
                                    title={""}
                                    description={product.description || ""}
                                    mediaUrl={product.image || "https://via.placeholder.com/300x200"}
                                    mediaType="image"
                                    onClick={() =>
                                        navigate(`/product-details`, { state: { product } })
                                    }
                                    cardsPerRow={procardsPerRow}
                                    cardsPerRowPhoneView={2}
                                />
                            ))}
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <Link
                                to="/category/all"
                                style={{ textDecoration: "none", color: "inherit" }}
                            >
                                <span>view more</span>
                            </Link>
                        </div>
                    </div>
                )
            ),
        },
    ];

    const sortedSections = sections.sort((a, b) => a.order - b.order);

    return (
        <div style={{ backgroundColor: bodyBackgroundColor }}>
            <Helmet>
                <title>Marriott Pharmaceutical</title>
                <meta name="description" content={seo?.metaDescription} />
                <meta name="keywords" content={seo?.metaKeywords} />
            </Helmet>

            {/* Search Bar Section */}
            <div
                className="home-search-container"
                style={{
                    top: 0,
                    zIndex: 100,
                }}
            >
                <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                    <SearchBar
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        placeholder="Search products..."
                    />
                </div>
            </div>

            {/* Search Results or Normal Content */}
            {showSearchResults ? (
                <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
                    <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
                        Search Results for "{searchTerm}"
                    </h2>
                    {searchResults.length > 0 ? (
                        <div
                            className="categories-list"
                            style={{
                                gridTemplateColumns: `repeat(${procardsPerRow}, 1fr)`,
                                gap: "20px",
                            }}
                        >
                            {searchResults.map((product, index) => (
                                <ProductCard
                                    key={index}
                                    title={""}
                                    description={product.description || ""}
                                    mediaUrl={product.image || "https://via.placeholder.com/300x200"}
                                    mediaType="image"
                                    onClick={() => navigate(`/product-details`, { state: { product } })}
                                    cardsPerRow={procardsPerRow}
                                    cardsPerRowPhoneView={2}
                                />
                            ))}
                        </div>
                    ) : (
                        <p style={{ textAlign: "center", fontSize: "1.2rem", padding: "40px 0" }}>
                            No products match your search
                        </p>
                    )}
                </div>
            ) : (
                // Render normal home page content
                <>
                    {sortedSections.map((section) => (
                        <div key={section.key} style={{ backgroundColor: section.backgroundColor }}>
                            {section.content}
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default HomePage;